// Vue Router 配置：用户后台 + 管理后台双套路由隔离

import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { message } from 'ant-design-vue'
import { useAuthStore } from '@/stores/auth'
import { useAdminAuthStore } from '@/stores/adminAuth'
import { useEntitlementsStore } from '@/stores/entitlements'
import { useLocaleStore } from '@/stores/locale'
import { useOperationsStore } from '@/stores/operations'
import { translate } from '@/composables/useLocale'
import { ADMIN_ACTIONS } from '@/config/adminActions'

// 用户后台路由（全部 iframe 嵌入 v1.9.10 原型，1:1 还原）
const userRoutes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/auth/Login.vue'),
    meta: { requiresAuth: false, title: '登录' },
  },
  {
    path: '/logout',
    name: 'Logout',
    component: () => import('@/views/auth/Logout.vue'),
    meta: { requiresAuth: false, title: '退出登录' },
  },
  {
    path: '/',
    redirect: '/home',
    component: () => import('@/layouts/UserLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      // 工作台
      { path: 'home', name: 'Dashboard', component: () => import('@/views/dashboard/Workbench.vue'), meta: { title: '工作台首页', permission: 'dashboard.w02:view' } },
      // 账号管理
      { path: 'accounts', name: 'Accounts', component: () => import('@/views/account/AccountList.vue'), meta: { title: '环境管理', permission: 'account.w03:view' } },
      { path: 'accounts/bind', name: 'BindAccount', redirect: '/accounts/import', meta: { title: '批量导入环境', permission: 'account.w06:view', planFeature: 'account_import' } },
      { path: 'accounts/import', name: 'BatchImport', component: () => import('@/views/account/BatchImport.vue'), meta: { title: '批量导入环境', permission: 'account.w06:view', planFeature: 'account_import' } },
      { path: 'accounts/groups', name: 'GroupManage', component: () => import('@/views/account/GroupManage.vue'), meta: { title: '环境分组管理', permission: 'account.w07b:view' } },
      { path: 'accounts/proxies', name: 'ProxyManage', component: () => import('@/views/account/ProxyManage.vue'), meta: { title: '代理管理', permission: 'account.w07c:view' } },
      { path: 'accounts/health', name: 'HealthAnalysis', component: () => import('@/views/account/HealthAnalysis.vue'), meta: { title: '账号健康分析', permission: ['account.w08b:view', 'account.w03:view'], planFeature: 'account_health' } },
      { path: 'accounts/:id', name: 'AccountDetail', component: () => import('@/views/account/AccountDetail.vue'), meta: { title: '环境详情', permission: ['account.w05:view', 'account.w03:detail'], breadcrumbParent: 'Accounts' } },
      // 养号中心
      { path: 'nurture/strategies', name: 'NurtureStrategies', component: () => import('@/views/nurture/StrategyList.vue'), meta: { title: '养号策略列表', permission: 'nurture.w07:view' } },
      { path: 'nurture/strategies/config', name: 'StrategyConfig', redirect: '/nurture/strategies?create=1', meta: { title: '策略配置', permission: 'nurture.w08:view' } },
      { path: 'nurture/strategies/config/:id', name: 'StrategyEdit', redirect: to => ({ path: '/nurture/strategies', query: { edit: String(to.params.id) } }), meta: { title: '编辑策略', permission: 'nurture.w08:view' } },
      { path: 'nurture/monitor', name: 'NurtureMonitor', component: () => import('@/views/nurture/Monitor.vue'), meta: { title: '养号监控', permission: 'nurture.w09:view' } },
      { path: 'nurture/console', name: 'ExecConsole', component: () => import('@/views/nurture/ExecConsole.vue'), meta: { title: '养号执行控制台', permission: 'nurture.w11b:view' } },
      { path: 'nurture/console/:id', name: 'ExecConsoleDetail', component: () => import('@/views/nurture/ExecConsole.vue'), meta: { title: '养号执行控制台', permission: 'nurture.w11b:view', breadcrumbParent: 'ExecConsole' } },
      { path: 'nurture/analytics', name: 'NurtureAnalytics', component: () => import('@/views/nurture/NurtureAnalytics.vue'), meta: { title: '养号效果分析', permission: 'nurture.w14:view' } },
      { path: 'nurture/health', name: 'NurtureHealth', redirect: '/accounts/health', meta: { title: '账号健康分析', permission: ['account.w08b:view', 'account.w03:view'], planFeature: 'account_health' } },
      // 内容素材
      { path: 'assets', name: 'Assets', component: () => import('@/views/content/Assets.vue'), meta: { title: '素材库管理', permission: 'content.w19:view' } },
      { path: 'content/domestic', name: 'DomesticOverview', redirect: '/publish/tasks' },
      ...([
        ['library', 'DomesticLibrary', '国内素材'],
        ['publish', 'DomesticPublish', '多平台发布'],
        ['tasks', 'DomesticTasks', '国内发布记录'],
        ['accounts', 'DomesticAccounts', '国内平台账号'],
        ['analytics', 'DomesticAnalytics', '国内数据统计'],
        ['devices', 'DomesticDevices', '本机连接'],
      ] as const).map(([section, name, title]) => ({ path: `content/domestic/${section}`, name,
        redirect: (to: any) => ({ path: ({publish:'/publish/new',tasks:'/publish/tasks',library:'/assets',accounts:'/publish/accounts',analytics:'/publish/analytics',devices:'/accounts'} as Record<string,string>)[section], query: section === 'publish' ? { ...to.query, platform: to.query.platform || 'zhihu' } : to.query }) })),
      { path: 'content/overseas', name: 'OverseasOperations', redirect: '/publish/tasks' },
      { path: 'ai-video', name: 'AiVideo', component: () => import('@/views/content/AiVideo.vue'), meta: { title: 'AI 视频创作', permission: 'content.w19a:view' } },
      { path: 'ai-video/permissions', name: 'AiVideoPermissions', component: () => import('@/views/content/AiVideo.vue'), meta: { title: '视频消费权限', permission: 'billing.ai_video:budget' } },
      { path: 'billing/ai-video', name: 'AiVideoWallet', component: () => import('@/views/content/AiVideo.vue'), meta: { title: '生成余额', permission: 'billing.ai_video:view' } },
      // 发布中心：素材快捷入口与历史链接均进入相同的编辑和查询页面。
      { path: 'publish/tasks', name: 'PublishTasks', component: () => import('@/views/publish/PublishCenter.vue'), meta: { title: '发布任务', permission: 'publish.w11:view', planFeature: 'auto_publish', centerView: 'tasks' } },
      { path: 'publish/new', name: 'PublishCreate', component: () => import('@/views/publish/PublishComposer.vue'), meta: { title: '新建发布', permission: 'content.w19:view', planFeature: 'auto_publish', breadcrumbParent: 'PublishTasks', domesticSection: 'publish', publishCenter: true } },
      { path: 'publish/results', name: 'PublishResults', component: () => import('@/views/publish/PublishCenter.vue'), meta: { title: '发布记录', permission: 'publish.w12:view', planFeature: 'auto_publish', centerView: 'records' } },
      { path: 'publish/rules', name: 'PublishRules', component: () => import('@/views/publish/PublishCenter.vue'), meta: { title: '自动发布规则', permission: 'publish.w11:view', planFeature: 'auto_publish', centerView: 'rules' } },
      { path: 'publish/schedule', name: 'PublishSchedule', component: () => import('@/views/publish/PublishSchedule.vue'), meta: { title: '智能排程', permission: 'publish.w11:view', planFeature: 'auto_publish' } },
      { path: 'publish/calendar', name: 'PublishCalendar', component: () => import('@/views/publish/PublishCenter.vue'), meta: { title: '发布日历', permission: 'publish.w11:view', planFeature: 'auto_publish', centerView: 'calendar' } },
      { path: 'publish/accounts', name: 'PublishAccounts', component: () => import('@/views/publish/PublishOperations.vue'), meta: { title: '平台账号', permission: 'account.w03:view', operationsView: 'accounts' } },
      { path: 'publish/analytics', name: 'PublishAnalytics', component: () => import('@/views/publish/PublishOperations.vue'), meta: { title: '数据分析', permission: ['account.w03:view','publish.w12:view'], operationsView: 'analytics' } },
      // 风控安全
      // 套餐仅限制功能和数据，页面入口由角色权限控制；预览页按真实权益挂载业务页面。
      { path: 'risk', name: 'RiskCenter', component: () => import('@/views/risk/RiskAccess.vue'), props: { page: 'alerts' }, meta: { title: '风险告警', permission: 'risk.w13b:view' } },
      { path: 'risk/rules', redirect: '/risk' },
      { path: 'risk/intel', name: 'RiskIntel', component: () => import('@/views/risk/RiskAccess.vue'), props: { page: 'intel' }, meta: { title: '情报库', permission: 'risk.w15:view' } },
      { path: 'risk/ban-rate', name: 'BanRate', component: () => import('@/views/risk/BanRate.vue'), meta: { title: '封禁率统计', permission: 'risk.w25:view' } },
      { path: 'risk/audit-logs', name: 'AuditLogs', component: () => import('@/views/system/AuditLogsAdmin.vue'), meta: { title: '审计日志', permission: 'system.w26:view' } },
      { path: 'risk/login-logs', name: 'LoginLogs', component: () => import('@/views/risk/LoginLogs.vue'), meta: { title: '登录日志', permission: 'risk.w30:view' } },
      // 计费中心
      { path: 'billing/plans', name: 'BillingPlans', component: () => import('@/views/billing/PlanCenter.vue'), meta: { title: '套餐中心', permission: 'billing.w17b:view' } },
      { path: 'billing/team-seats', name: 'BillingTeamSeats', component: () => import('@/views/billing/TeamSeats.vue'), meta: { title: '团队席位', permission: 'billing.w17c:view' } },
      { path: 'billing/usage', name: 'BillingUsage', component: () => import('@/views/billing/ResourceUsage.vue'), meta: { title: '资源使用情况', permission: 'billing.w31:view' } },
      { path: 'billing/invoices', name: 'BillingInvoices', component: () => import('@/views/billing/InvoiceHistory.vue'), meta: { title: '历史账单', permission: 'billing.w32:view' } },
      // 系统管理
      { path: 'system/roles', name: 'Roles', component: () => import('@/views/system/RolePermission.vue'), meta: { title: '角色权限管理', permission: 'system.w27:view' } },
      { path: 'system/settings', name: 'Settings', component: () => import('@/views/system/Settings.vue'), meta: { title: '系统设置', permission: 'system.w27b:view' } },
      { path: 'system/export', name: 'DataExport', component: () => import('@/views/system/DataExport.vue'), meta: { title: '数据导出', permission: 'system.w28:view' } },
      { path: 'system/audit-logs', name: 'SystemAuditLogs', component: () => import('@/views/system/AuditLogsAdmin.vue'), meta: { title: '审计日志', permission: 'system.w26:view' } },
      { path: 'system/notifications', name: 'Notifications', component: () => import('@/views/system/Notifications.vue'), meta: { title: '通知中心', permission: 'system.w29:view' } },
      { path: 'profile', name: 'Profile', component: () => import('@/views/system/Profile.vue'), meta: { title: '个人中心', permission: 'system.w33:view' } },
      { path: 'forbidden', name: 'AccessDenied', component: () => import('@/views/AccessDenied.vue'), meta: { title: '无权访问' } },
      // fallback：直接展示任意原型文件 (?f=Wxx.html)
      { path: 'prototype', name: 'Prototype', component: () => import('@/views/PrototypeView.vue'), meta: { title: '原型预览', permission: 'dashboard.w02:view' } },
    ],
  },
]

// 管理后台路由（租户后台）
const adminRoutes: RouteRecordRaw[] = [
  {
    path: '/admin/login',
    name: 'AdminLogin',
    component: () => import('@/views/auth/AdminLogin.vue'),
    meta: { requiresAuth: false, title: '管理员登录' },
  },
  {
    path: '/admin',
    component: () => import('@/layouts/AdminLayout.vue'),
    meta: { requiresAuth: true, isAdmin: true },
    children: [
      { path: '', name: 'AdminOverview', component: () => import('@/views/admin/Overview.vue'), meta: { title: '运营概览', adminAction: ADMIN_ACTIONS.OVERVIEW_VIEW } },
      { path: 'tenants', name: 'AdminTenants', component: () => import('@/views/admin/TenantList.vue'), meta: { title: '租户管理', adminAction: ADMIN_ACTIONS.TENANT_VIEW } },
      { path: 'tenants/:id', name: 'AdminTenantDetail', component: () => import('@/views/admin/TenantDetail.vue'), meta: { title: '租户详情', adminAction: ADMIN_ACTIONS.TENANT_VIEW } },
      { path: 'users', name: 'AdminUsers', component: () => import('@/views/admin/UserList.vue'), meta: { title: '用户管理', adminAction: ADMIN_ACTIONS.USER_VIEW } },
      { path: 'users/:id', name: 'AdminUserDetail', component: () => import('@/views/admin/UserDetail.vue'), meta: { title: '用户详情', adminAction: ADMIN_ACTIONS.USER_VIEW } },
      { path: 'environments', name: 'AdminEnvs', component: () => import('@/views/admin/EnvOverview.vue'), meta: { title: '环境总览', adminAction: ADMIN_ACTIONS.ENVIRONMENT_VIEW } },
      { path: 'proxies', name: 'AdminProxies', component: () => import('@/views/admin/ProxyOverview.vue'), meta: { title: '代理资源', adminAction: ADMIN_ACTIONS.PROXY_VIEW } },
      { path: 'nurture', name: 'AdminNurture', component: () => import('@/views/admin/AdminWorkspace.vue'), meta: { title: '养号运营', workspace: 'nurture', adminAction: ADMIN_ACTIONS.NURTURE_VIEW } },
      { path: 'nurture/tasks', name: 'AdminNurtureTasks', redirect: { path: '/admin/nurture', query: { tab: 'tasks' } }, meta: { adminAction: ADMIN_ACTIONS.NURTURE_VIEW } },
      { path: 'nurture/analytics', name: 'AdminNurtureAnalytics', redirect: { path: '/admin/nurture', query: { tab: 'analytics' } }, meta: { adminAction: ADMIN_ACTIONS.NURTURE_VIEW } },
      { path: 'publish', name: 'AdminPublish', component: () => import('@/views/admin/AdminWorkspace.vue'), meta: { title: '发布运营', workspace: 'publish', adminAction: ADMIN_ACTIONS.PUBLISH_VIEW } },
      { path: 'publish/tasks', name: 'AdminPublishTasks', redirect: { path: '/admin/publish', query: { tab: 'tasks' } }, meta: { adminAction: ADMIN_ACTIONS.PUBLISH_VIEW } },
      { path: 'publish/stats', name: 'AdminPublishStats', redirect: { path: '/admin/publish', query: { tab: 'stats' } }, meta: { adminAction: ADMIN_ACTIONS.PUBLISH_VIEW } },
      { path: 'risk', name: 'AdminRisk', component: () => import('@/views/admin/AdminWorkspace.vue'), meta: { title: '风控中心', workspace: 'risk', adminAction: ADMIN_ACTIONS.RISK_VIEW } },
      { path: 'risk/events', name: 'AdminRiskEvents', redirect: { path: '/admin/risk', query: { tab: 'events' } }, meta: { adminAction: ADMIN_ACTIONS.RISK_VIEW } },
      { path: 'risk/ban-rate', name: 'AdminBanRate', redirect: { path: '/admin/risk', query: { tab: 'ban-rate' } }, meta: { adminAction: ADMIN_ACTIONS.RISK_VIEW } },
      { path: 'risk/intel', name: 'AdminIntel', component: () => import('@/views/admin/IntelAdmin.vue'), meta: { title: '情报库管理', adminAction: ADMIN_ACTIONS.INTEL_VIEW } },
      { path: 'security', name: 'AdminSecurity', component: () => import('@/views/admin/AdminWorkspace.vue'), meta: { title: '安全日志', workspace: 'security', adminAction: ADMIN_ACTIONS.SECURITY_VIEW } },
      { path: 'risk/login-logs', name: 'AdminLoginLogs', redirect: { path: '/admin/security', query: { tab: 'login' } }, meta: { adminAction: ADMIN_ACTIONS.SECURITY_VIEW } },
      { path: 'audit-logs', name: 'AdminAuditLogs', redirect: { path: '/admin/security', query: { tab: 'audit' } }, meta: { adminAction: ADMIN_ACTIONS.SECURITY_VIEW } },
      { path: 'orders', name: 'AdminOrders', component: () => import('@/views/admin/OrderCenter.vue'), meta: { title: '订单中心', adminAction: ADMIN_ACTIONS.ORDER_VIEW } },
      { path: 'billing/plans', name: 'AdminPlans', component: () => import('@/views/admin/PlanAdmin.vue'), meta: { title: '套餐管理', adminAction: ADMIN_ACTIONS.PLAN_VIEW } },
      { path: 'billing/subscriptions', name: 'AdminSubscriptions', component: () => import('@/views/admin/SubscriptionAdmin.vue'), meta: { title: '订阅管理', adminAction: ADMIN_ACTIONS.SUBSCRIPTION_VIEW } },
      { path: 'billing/invoices', name: 'AdminInvoices', component: () => import('@/views/admin/InvoiceAdmin.vue'), meta: { title: '账单管理', adminAction: ADMIN_ACTIONS.INVOICE_VIEW } },
      { path: 'finance', name: 'AdminFinance', component: () => import('@/views/admin/AdminWorkspace.vue'), meta: { title: '资源与收入', workspace: 'finance', adminAction: ADMIN_ACTIONS.FINANCE_VIEW } },
      { path: 'billing/usage', name: 'AdminUsage', redirect: { path: '/admin/finance', query: { tab: 'usage' } }, meta: { adminAction: ADMIN_ACTIONS.FINANCE_VIEW } },
      { path: 'billing/revenue', name: 'AdminRevenue', redirect: { path: '/admin/finance', query: { tab: 'revenue' } }, meta: { adminAction: ADMIN_ACTIONS.FINANCE_VIEW } },
      { path: 'operators', name: 'AdminOperators', component: () => import('@/views/admin/RoleAdmin.vue'), meta: { title: '平台管理员', adminAction: ADMIN_ACTIONS.ADMIN_USER_VIEW } },
      { path: 'roles', name: 'AdminRoles', redirect: '/admin/operators', meta: { adminAction: ADMIN_ACTIONS.ADMIN_USER_VIEW } },
      { path: 'settings', name: 'AdminSettings', component: () => import('@/views/admin/SettingsAdmin.vue'), meta: { title: '平台设置', adminAction: ADMIN_ACTIONS.SETTINGS_VIEW } },
      { path: 'notifications', name: 'AdminNotifications', component: () => import('@/views/admin/NotificationAdmin.vue'), meta: { title: '通知投递', adminAction: ADMIN_ACTIONS.NOTIFICATION_VIEW } },
      { path: 'portal', name: 'AdminPortal', component: () => import('@/views/admin/PortalAdmin.vue'), meta: { title: '门户客服与 FAQ', adminAction: ADMIN_ACTIONS.SUPPORT_VIEW } },
      { path: 'export', name: 'AdminExport', component: () => import('@/views/admin/ExportAdmin.vue'), meta: { title: '导出任务', adminAction: ADMIN_ACTIONS.EXPORT_VIEW } },
      { path: 'forbidden', name: 'AdminAccessDenied', component: () => import('@/views/AccessDenied.vue'), meta: { title: '无权访问' } },
    ],
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes: [...userRoutes, ...adminRoutes],
})

// 全局路由守卫：认证 + 权限
router.beforeEach(async (to, _from) => {
  const authStore = useAuthStore()
  const localeStore = useLocaleStore()
  const t = (key: string) => translate(localeStore.locale, key)

  // 设置页面标题
  if (to.meta.title) {
    const title = to.path.startsWith('/admin')
      ? String(to.meta.title)
      : translate(localeStore.locale, `route.${String(to.name || '')}`, {}, String(to.meta.title))
    document.title = `${title} - TikFlow`
  }

  // 不需要认证的页面
  if (to.meta.requiresAuth === false) {
    return
  }

  // 管理后台使用独立令牌，不依赖用户后台登录状态。
  if (to.meta.isAdmin) {
    const adminAuthStore = useAdminAuthStore()
    if (!adminAuthStore.isLoggedIn) {
      return { path: '/admin/login', query: { redirect: to.fullPath } }
    }
    if (!adminAuthStore.profileLoaded) {
      try {
        await adminAuthStore.fetchMe()
      } catch {
        adminAuthStore.clearSession()
        return { path: '/admin/login', query: { redirect: to.fullPath } }
      }
    }
    const requiredAction = String(to.meta.adminAction || '')
    if (requiredAction && !adminAuthStore.hasAction(requiredAction)) {
      message.warning('当前管理员没有访问该页面的权限')
      return '/admin/forbidden'
    }
    return
  }

  // 用户后台需要认证但未登录
  const operations = useOperationsStore()
  try { await operations.load() } catch {
    if (to.path !== '/forbidden') { message.error('无法读取平台配置，请稍后重试'); return '/forbidden' }
    return
  }
  if (!operations.supports('tiktok') && (to.path.startsWith('/nurture') || to.path.startsWith('/accounts/health') || to.path === '/publish/schedule')) return '/forbidden'

  if (!authStore.isLoggedIn) {
    try {
      if (await authStore.restorePortalSession()) return
    } catch {
      // 没有可恢复的门户会话，继续进入标准登录流程。
    }
    return { path: '/login', query: { redirect: to.fullPath } }
  }

  // 用户后台页面权限：刷新页面时先恢复 profile，再决定是否允许进入。
  const requiredPermissions = Array.isArray(to.meta.permission)
    ? to.meta.permission.map(String)
    : (to.meta.permission ? [String(to.meta.permission)] : [])
  if (!to.meta.isAdmin && requiredPermissions.length) {
    if (!authStore.user || !Array.isArray(authStore.user.permissions)) {
      try {
        await authStore.fetchUser()
      } catch {
        return '/login'
      }
    }
    if (authStore.user?.mfa_enforcement_mode === 'required' && to.path !== '/profile') {
      message.warning(t('guard.mfa_required'))
      return '/profile?mfa=required'
    }
    if (
      !(authStore.user?.mfa_enforcement_mode === 'required' && to.path === '/profile')
      && !requiredPermissions.every(code => authStore.hasPermission(code))
    ) {
      message.warning(t('guard.permission_denied'))
      return '/forbidden'
    }
  }

  if (!to.meta.isAdmin && authStore.user?.mfa_enforcement_mode === 'required' && to.path !== '/profile') {
    message.warning(t('guard.mfa_required'))
    return '/profile?mfa=required'
  }

  const requiredFeature = String(to.meta.planFeature || '')
  if (!to.meta.isAdmin && requiredFeature) {
    const entitlementsStore = useEntitlementsStore()
    try {
      await entitlementsStore.load()
    } catch {
      message.error(t('guard.entitlements_failed'))
      return authStore.hasPermission('dashboard.w02:view') ? '/home' : '/forbidden'
    }
    if (!entitlementsStore.hasFeature(requiredFeature)) {
      message.warning(t('guard.feature_unavailable'))
      return authStore.hasPermission('billing.w17b:view') ? '/billing/plans' : '/forbidden'
    }
  }
})

export default router
